/**
 * systems-dialog component
 *
 * Renders a "Create organization" form dialog floating over a live
 * code-scrolling animation. Matches the visual in systems-05.png.
 * Desktop only — mobile falls back to the systems-05.png static image.
 *
 * @param {object} props - (unused)
 * @param {number} index - Panel index for stagger animation
 * @returns {HTMLElement|null}
 */

import './systems-dialog.css';
import { PANEL_ANIMATION_DELAY_MS } from '../constants.js';

const DESKTOP_QUERY = '(pointer: fine) and (min-width: 769px)';

export function createSystemsDialog(props, index) {
  try {
    const isDesktop = window.matchMedia(DESKTOP_QUERY).matches;
    if (!isDesktop) return null;

    const panel = document.createElement('div');
    panel.className = 'portfolio-panel portfolio-panel--component portfolio-panel--no-border';
    panel.classList.remove('skeleton-loading');

    buildScene(panel);
    setTimeout(
      () => panel.classList.add('animated'),
      PANEL_ANIMATION_DELAY_MS * (index || 0),
    );
    return panel;
  } catch (err) {
    console.error('[systems-dialog] render failed:', err);
    return null;
  }
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function el(tag, className, text) {
  const e = document.createElement(tag);
  if (className) e.className = className;
  if (text !== undefined) e.textContent = text;
  return e;
}

// ---------------------------------------------------------------------------
// Code background
// ---------------------------------------------------------------------------

// Each line is an array of {t: text, c: optional CSS class} segments.
// Empty array = blank line.
const CODE_LINES = [
  [{ t: 'import', c: 'sdc-kw' }, { t: ' { Eye, EyeOff, LockIcon } ' }, { t: 'from', c: 'sdc-kw' }, { t: ' ' }, { t: '"lucide-react"', c: 'sdc-str' }, { t: ';' }],
  [{ t: 'import', c: 'sdc-kw' }, { t: ' * ' }, { t: 'as', c: 'sdc-kw' }, { t: ' React ' }, { t: 'from', c: 'sdc-kw' }, { t: ' ' }, { t: '"react"', c: 'sdc-str' }, { t: ';' }],
  [{ t: 'import', c: 'sdc-kw' }, { t: ' { cn } ' }, { t: 'from', c: 'sdc-kw' }, { t: ' ' }, { t: '"@/lib/utils"', c: 'sdc-str' }, { t: ';' }],
  [],
  [{ t: 'interface', c: 'sdc-kw' }, { t: ' ' }, { t: 'InputProps', c: 'sdc-type' }, { t: ' extends React.' }, { t: 'InputHTMLAttributes', c: 'sdc-type' }, { t: '<' }, { t: 'HTMLInputElement', c: 'sdc-type' }, { t: '> {' }],
  [{ t: '  icon', c: 'sdc-prop' }, { t: '?: React.' }, { t: 'ReactNode', c: 'sdc-type' }, { t: ';' }],
  [{ t: '  type', c: 'sdc-prop' }, { t: '?: ' }, { t: 'string', c: 'sdc-type' }, { t: ';' }],
  [{ t: '  placeholder', c: 'sdc-prop' }, { t: '?: ' }, { t: 'string', c: 'sdc-type' }, { t: ';' }],
  [{ t: '}' }],
  [],
  [{ t: 'const', c: 'sdc-kw' }, { t: ' Input = React.' }, { t: 'forwardRef', c: 'sdc-fn' }, { t: '<' }, { t: 'HTMLInputElement', c: 'sdc-type' }, { t: ', ' }, { t: 'InputProps', c: 'sdc-type' }, { t: '>(' }],
  [{ t: '  ({ className, icon, type, placeholder, ...props }, ref) => {' }],
  [{ t: '    const', c: 'sdc-kw' }, { t: ' [hasValue, setHasValue] = React.' }, { t: 'useState', c: 'sdc-fn' }, { t: '(' }],
  [{ t: '      props.value || props.defaultValue,' }],
  [{ t: '    );' }],
  [{ t: '    const', c: 'sdc-kw' }, { t: ' [showPassword, setShowPassword] = React.' }, { t: 'useState', c: 'sdc-fn' }, { t: '(' }, { t: 'false', c: 'sdc-kw' }, { t: ');' }],
  [],
  [{ t: '    const', c: 'sdc-kw' }, { t: ' togglePassword = () => {' }],
  [{ t: '      setShowPassword(!showPassword);' }],
  [{ t: '    };' }],
  [],
  [{ t: '    const', c: 'sdc-kw' }, { t: ' handleChange = (e: React.' }, { t: 'ChangeEvent', c: 'sdc-type' }, { t: '<' }, { t: 'HTMLInputElement', c: 'sdc-type' }, { t: '>) => {' }],
  [{ t: '      if (e.target.value.length > 0) {' }],
  [{ t: '        setHasValue(' }, { t: 'true', c: 'sdc-kw' }, { t: ');' }],
  [{ t: '      }' }],
  [{ t: '      if (props.onChange) {' }],
  [{ t: '        props.' }, { t: 'onChange', c: 'sdc-fn' }, { t: '(e);' }],
  [{ t: '      }' }],
  [{ t: '    };' }],
  [],
  [{ t: '    return', c: 'sdc-kw' }, { t: ' ' }, { t: 'cn', c: 'sdc-fn' }, { t: '(' }],
  [{ t: '      "block h-fit w-full text-sm group",' }],
  [{ t: '      className ? className : "",' }],
  [{ t: '    );' }],
  [{ t: '  },' }],
  [{ t: ');' }],
  [],
  [{ t: 'export', c: 'sdc-kw' }, { t: ' ' }, { t: 'default', c: 'sdc-kw' }, { t: ' Input;' }],
  [],
  [{ t: 'const', c: 'sdc-kw' }, { t: ' ' }, { t: 'OrgSettings', c: 'sdc-fn' }, { t: ' = () => {' }],
  [{ t: '  const', c: 'sdc-kw' }, { t: ' [orgName, setOrgName] = ' }, { t: 'useState', c: 'sdc-fn' }, { t: '("", "");' }],
  [{ t: '  const', c: 'sdc-kw' }, { t: ' [enterprise, setEnterprise] = ' }, { t: 'useState', c: 'sdc-fn' }, { t: '("Enterprise A");' }],
  [],
  [{ t: '  const', c: 'sdc-kw' }, { t: ' handleSubmit = async (e) => {' }],
  [{ t: '    e.preventDefault();' }],
  [{ t: '    await createOrganization({ name: orgName, enterprise });' }],
  [{ t: '  };' }],
  [],
  [{ t: '  return', c: 'sdc-kw' }, { t: ' (' }],
  [{ t: '    <' }, { t: 'Dialog', c: 'sdc-type' }, { t: ' open={isOpen} onOpenChange={setIsOpen}>' }],
  [{ t: '      <' }, { t: 'DialogContent', c: 'sdc-type' }, { t: '>' }],
  [{ t: '        <' }, { t: 'DialogTitle', c: 'sdc-type' }, { t: '>Create organization</' }, { t: 'DialogTitle', c: 'sdc-type' }, { t: '>' }],
  [{ t: '        <' }, { t: 'form', c: 'sdc-kw' }, { t: ' onSubmit={handleSubmit}>' }],
  [{ t: '          <' }, { t: 'Input', c: 'sdc-type' }, { t: ' id="org-name" value={orgName}' }],
  [{ t: '            onChange={(e) => setOrgName(e.target.value)}' }],
  [{ t: '            placeholder="new-org" />' }],
  [{ t: '        </' }, { t: 'form', c: 'sdc-kw' }, { t: '>' }],
  [{ t: '      </' }, { t: 'DialogContent', c: 'sdc-type' }, { t: '>' }],
  [{ t: '    </' }, { t: 'Dialog', c: 'sdc-type' }, { t: '>' }],
  [{ t: '  );' }],
  [{ t: '};' }],
  [],
];

const CHARS_PER_SEC = 220;
const MAX_VISIBLE = 40;

/**
 * Build a single code line element. Segments with a class use <span>, plain
 * text uses bare text nodes so the typewriter can target them directly.
 * Returns { lineEl, textNodes } where textNodes is an ordered array of the
 * text nodes whose content will be revealed character-by-character.
 */
function buildCodeLine(segments) {
  const line = el('div', 'sdc-line');
  const textNodes = [];
  for (const seg of segments) {
    if (seg.c) {
      const span = el('span', seg.c);
      const tn = document.createTextNode('');
      span.appendChild(tn);
      line.appendChild(span);
      textNodes.push({ node: tn, full: seg.t });
    } else {
      const tn = document.createTextNode('');
      line.appendChild(tn);
      textNodes.push({ node: tn, full: seg.t });
    }
  }
  return { lineEl: line, textNodes };
}

function buildCodeBg(scene) {
  const bg = el('div', 'sdc-code-bg');
  const wrap = el('div', 'sdc-lines-wrap');
  bg.appendChild(wrap);
  scene.appendChild(bg);

  let codeIdx = 0;
  // queue of text nodes yet to be typed on the current/upcoming lines
  let typeQueue = []; // [{node, full, charsDone}]
  let charAccum = 0;

  function addLine(instant) {
    const segments = CODE_LINES[codeIdx % CODE_LINES.length];
    codeIdx++;

    const isBlank = segments.length === 0;
    const { lineEl, textNodes } = buildCodeLine(segments);
    wrap.appendChild(lineEl);

    while (wrap.children.length > MAX_VISIBLE) {
      wrap.removeChild(wrap.firstChild);
    }

    if (instant || isBlank) {
      // Reveal immediately — fill all text nodes now
      for (const entry of textNodes) {
        entry.node.textContent = entry.full;
      }
    } else {
      for (const entry of textNodes) {
        typeQueue.push({ node: entry.node, full: entry.full, charsDone: 0 });
      }
    }
  }

  // Pre-fill all but the last line so background has content on first paint
  for (let i = 0; i < MAX_VISIBLE - 1; i++) addLine(true);
  // Start typing the first live line
  addLine(false);

  let rafId = null;
  let lastTs = null;

  function tick(ts) {
    if (lastTs !== null) {
      const dt = Math.min(ts - lastTs, 100); // cap dt to avoid huge jumps
      charAccum += (dt / 1000) * CHARS_PER_SEC;
    }
    lastTs = ts;

    // Distribute accumulated characters across the type queue
    while (charAccum >= 1 && typeQueue.length > 0) {
      const entry = typeQueue[0];
      const remaining = entry.full.length - entry.charsDone;
      const toReveal = Math.min(Math.floor(charAccum), remaining);
      entry.charsDone += toReveal;
      entry.node.textContent = entry.full.slice(0, entry.charsDone);
      charAccum -= toReveal;

      if (entry.charsDone >= entry.full.length) {
        typeQueue.shift();
      } else {
        break;
      }
    }

    // When the current line finishes typing, queue the next line
    if (typeQueue.length === 0) {
      charAccum = 0;
      addLine(false);
    }

    rafId = requestAnimationFrame(tick);
  }

  // Pause animation when panel scrolls out of view
  const observer = new IntersectionObserver(
    ([entry]) => {
      if (entry.isIntersecting) {
        if (rafId === null) {
          lastTs = null;
          rafId = requestAnimationFrame(tick);
        }
      } else {
        if (rafId !== null) {
          cancelAnimationFrame(rafId);
          rafId = null;
        }
      }
    },
    { threshold: 0 },
  );
  observer.observe(scene);
}

// ---------------------------------------------------------------------------
// Lock icon SVG
// ---------------------------------------------------------------------------

function buildLockIcon() {
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('width', '14');
  svg.setAttribute('height', '14');
  svg.setAttribute('viewBox', '0 0 24 24');
  svg.setAttribute('fill', 'none');
  svg.setAttribute('stroke', 'currentColor');
  svg.setAttribute('stroke-width', '2');
  svg.setAttribute('stroke-linecap', 'round');
  svg.setAttribute('stroke-linejoin', 'round');
  svg.setAttribute('class', 'sdc-lock-icon');

  const rect = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
  rect.setAttribute('x', '3'); rect.setAttribute('y', '11');
  rect.setAttribute('width', '18'); rect.setAttribute('height', '11');
  rect.setAttribute('rx', '2'); rect.setAttribute('ry', '2');
  svg.appendChild(rect);

  const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
  path.setAttribute('d', 'M7 11V7a5 5 0 0 1 10 0v4');
  svg.appendChild(path);

  return svg;
}

// ---------------------------------------------------------------------------
// Dialog
// ---------------------------------------------------------------------------

function buildField(label, required, opts = {}) {
  const field = el('div', 'sdc-field');

  const labelEl = el('label', 'sdc-field-label', label);
  if (required) labelEl.appendChild(el('span', 'sdc-required', ' *'));
  field.appendChild(labelEl);

  const inputWrap = el('div', 'sdc-input-wrap');
  const input = document.createElement('input');
  input.type = 'text';
  input.className = 'sdc-input';
  if (opts.value) input.value = opts.value;
  if (opts.placeholder) input.placeholder = opts.placeholder;
  if (opts.locked) input.disabled = true;
  inputWrap.appendChild(input);
  if (opts.locked) inputWrap.appendChild(buildLockIcon());
  field.appendChild(inputWrap);

  if (opts.hint) field.appendChild(el('div', 'sdc-field-hint', opts.hint));
  return field;
}

function buildDialog(scene) {
  const dialog = el('div', 'sdc-dialog');

  dialog.appendChild(el('div', 'sdc-dialog-title', 'Create organization'));

  dialog.appendChild(buildField('Organization name', true, {
    value: 'new-org',
    placeholder: 'new-org',
  }));
  dialog.appendChild(buildField('Organization description', false, {
    placeholder: 'Frontend platform team supporting all web apps',
  }));
  dialog.appendChild(buildField('Enterprise name', true, {
    value: 'Enterprise A',
    locked: true,
    hint: 'Your new organization will be created in this enterprise.',
  }));
  dialog.appendChild(buildField('Organization email', true, {
    value: 'admin@email.co',
    locked: true,
    hint: 'This email is inherited from your enterprise and can be changed later.',
  }));

  const actions = el('div', 'sdc-actions');
  actions.appendChild(el('button', 'sdc-btn-cancel', 'Cancel'));
  actions.appendChild(el('button', 'sdc-btn-primary', 'Create organization'));
  dialog.appendChild(actions);

  scene.appendChild(dialog);
}

// ---------------------------------------------------------------------------
// Scene assembly
// ---------------------------------------------------------------------------

function buildScene(container) {
  const scene = el('div', 'sdc-scene');
  // Prevent wheel events from bubbling to the page when hovering over the scene
  scene.addEventListener('wheel', (e) => e.preventDefault(), { passive: false });
  buildCodeBg(scene);
  buildDialog(scene);
  container.appendChild(scene);
}
